import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import echo from "@/lib/echo";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { bumpMentionCount, currentTenantId } from "@/lib/command-center-badges";
import {
  COMMAND_CENTER_MENTION_EVENT,
  COMMAND_CENTER_MESSAGE_POSTED_EVENT,
  commandCenterChannelName,
  commandCenterUserChannelName,
} from "@/lib/command-center-realtime";
import { insertMessage } from "@/hooks/command-center/use-command-center-messages-query";
import type { CommandCenterMessage } from "@/services/command-center";
import { COMMAND_CENTER_COPY as copy } from "./command-center-copy";

const COMMAND_CENTER_PATH = "/centro-de-comando";

type MentionPayload = { by?: { name?: unknown }; action_path?: unknown };

function mentionAuthor(payload: unknown): string {
  const by = (payload as MentionPayload)?.by;
  return typeof by?.name === "string" && by.name !== "" ? by.name : "Alguém";
}

function mentionPath(payload: unknown): string {
  const path = (payload as MentionPayload)?.action_path;
  return typeof path === "string" && path.startsWith("/") ? path : COMMAND_CENTER_PATH;
}

function isMessage(payload: unknown): payload is CommandCenterMessage {
  return typeof payload === "object" && payload !== null && typeof (payload as { id?: unknown }).id === "string";
}

/**
 * Mounted once at the app shell, next to `HitlPusherBridge`.
 *
 * A mention has to reach the clinic on whatever screen it is on, so the per-user
 * private channel is joined here and not on the command center page: a
 * page-level subscription would stop counting exactly when the badge matters.
 * The tenant channel is joined too, so the unread pill keeps a real count while
 * the thread is closed — the same `insertMessage` the page uses, deduped by id.
 */
export function CommandCenterMentionBridge() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  // The clinic can be switched without a reload, and the switch is a
  // `localStorage` write: a tenant read once at mount would keep the bridge on
  // the previous clinic's channel, counting mentions the sidebar never shows.
  const [tenantId, setTenantId] = useState<string | null>(() => currentTenantId());

  useEffect(() => {
    const sync = () => setTenantId(currentTenantId());
    sync();
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, [userId]);

  useEffect(() => {
    if (!echo || !userId || !tenantId) return;

    const channel = echo.private(commandCenterUserChannelName(userId));
    const onMentioned = (payload: unknown) => {
      bumpMentionCount(tenantId);
      const path = mentionPath(payload);
      toast({
        title: copy.mentionToastTitle,
        description: copy.mentionToastBody(mentionAuthor(payload)),
        action: { label: copy.mentionToastAction, onClick: () => navigate(path) },
      });
    };
    channel.listen(COMMAND_CENTER_MENTION_EVENT, onMentioned);

    return () => {
      channel.stopListening(COMMAND_CENTER_MENTION_EVENT, onMentioned);
    };
  }, [navigate, tenantId, userId]);

  useEffect(() => {
    if (!echo || !tenantId) return;

    const channel = echo.private(commandCenterChannelName(tenantId));
    const onPosted = (payload: unknown) => {
      if (isMessage(payload)) insertMessage(queryClient, payload);
    };
    channel.listen(COMMAND_CENTER_MESSAGE_POSTED_EVENT, onPosted);

    return () => {
      channel.stopListening(COMMAND_CENTER_MESSAGE_POSTED_EVENT, onPosted);
    };
  }, [queryClient, tenantId]);

  return null;
}
